<template>
  <Teleport to="body">
    <div class="toast-area">
      <ToastComponent :visible="toastVisible" :type="toastType" :message="toastMessage" />
    </div>
  </Teleport>

  <Teleport to="body">
    <div v-if="isOpen" class="set-value-modal-overlay">
      <div
        class="set-value-modal"
        :class="{
          'modal-baseline': itemInfo?.type === 'Baseline',
          'modal-routine': itemInfo?.type === 'Routine',
        }"
        @click.stop
      >
      <div class="modal-header">
        <div class="title-section">
          <h2 class="modal-title">Set Value - {{ itemInfo?.title }}</h2>
          <span v-if="itemInfo?.type === 'Routine'" class="tag tag-routine">Routine</span>
          <span v-else-if="itemInfo?.type === 'Baseline'" class="tag tag-baseline">Baseline</span>
        </div>
        <button class="close-btn" type="button" @click.stop="handleClose">
          <img :src="fillIcon" alt="close" class="close-icon" />
        </button>
      </div>
      <div class="header-spacing"></div>

      <div class="modal-body" @click="closeAllFieldTypeDropdowns">
        <template v-if="itemInfo?.type === 'Baseline'">
          <div
            v-for="(component, index) in components"
            :key="index"
            class="component-block"
            @dragover="handleComponentDragOver"
            @drop="(e) => handleComponentDrop(e, index)"
          >
            <div class="component-header">
              <div class="component-header-content">
                <div
                  class="component-drag-handle"
                  draggable="true"
                  @dragstart="(e) => handleComponentDragStart(e, index)"
                  @dragend="handleComponentDragEnd"
                >
                  <img :src="dragIcon" alt="drag" class="drag-icon" />
                </div>
                <div class="component-title-section">
                  <span class="component-number">#{{ index + 1 }}</span>
                  <span class="required-mark">*</span>
                  <span class="component-label">Component</span>
                </div>
              </div>
              <button class="component-delete-btn" @click.stop="deleteComponent(index)">
                <img :src="noIcon" alt="delete" class="delete-icon" />
              </button>
            </div>

            <div class="component-fields">
              <div class="component-field" :class="{ 'has-error': component.nameHasError }">
                <div class="field-input-wrapper">
                  <input
                    :value="component.name"
                    type="text"
                    class="field-input"
                    :class="{ 'input-error': component.nameHasError }"
                    placeholder="Component (max 300 characters)"
                    @input="(e) => handleComponentNameInput(index, (e.target as HTMLInputElement).value)"
                  />
                  <img v-if="component.nameHasError" :src="alertIcon" alt="alert" class="field-alert-icon" />
                </div>
                <div v-if="component.nameHasError" class="error-wrapper">
                  <span class="error-text">{{
                    component.name.trim()
                      ? 'Please keep it under 300 characters'
                      : 'Please complete all required fields.'
                  }}</span>
                </div>
              </div>

              <div
                v-if="fieldTypeRequiresValues(component.fieldType)"
                class="component-field"
                :class="{ 'has-error': component.valuesHasError }"
              >
                <div class="field-label">
                  <span class="required-mark">*</span>
                  <span>Values</span>
                </div>
                <div class="field-input-wrapper">
                  <input
                    :value="component.values"
                    type="text"
                    class="field-input"
                    :class="{ 'input-error': component.valuesHasError }"
                    placeholder="No values "
                    @input="(e) => handleComponentValuesInput(index, (e.target as HTMLInputElement).value)"
                  />
                  <img v-if="component.valuesHasError" :src="alertIcon" alt="alert" class="field-alert-icon" />
                </div>
                <div v-if="component.valuesHasError" class="error-wrapper">
                  <span class="error-text">{{
                    component.values.trim()
                      ? 'Please keep it under 300 characters'
                      : 'Please complete all required fields.'
                  }}</span>
                </div>
              </div>

              <div class="component-field" :class="{ 'has-error': component.fieldTypeHasError }">
                <div class="field-label">
                  <span class="required-mark">*</span>
                  <span>Field Type</span>
                </div>
                <div class="field-input-wrapper">
                  <div class="field-select-wrapper" @click.stop="toggleFieldTypeDropdown(index)">
                    <input
                      type="text"
                      class="field-input"
                      :class="{
                        'input-error': component.fieldTypeHasError,
                        'is-placeholder': !component.fieldType,
                      }"
                      :value="getFieldTypeLabel(component.fieldType)"
                      readonly
                    />
                    <img
                      :src="downarrowIcon"
                      alt="arrow"
                      class="select-arrow"
                      :class="{ 'select-arrow--open': fieldTypeDropdownOpen[index] }"
                    />
                    <div v-if="fieldTypeDropdownOpen[index]" class="dropdown-panel" @click.stop>
                      <div
                        v-for="option in fieldTypeOptions"
                        :key="option.value"
                        class="dropdown-option"
                        :class="{ active: isFieldTypeSelected(component.fieldType, option.value) }"
                        @click="selectFieldType(index, option.value)"
                      >
                        <span>{{ option.label }}</span>
                      </div>
                    </div>
                  </div>
                  <img
                    v-if="component.fieldTypeHasError"
                    :src="alertIcon"
                    alt="alert"
                    class="field-alert-icon field-alert-icon-select"
                  />
                </div>
                <div v-if="component.fieldTypeHasError" class="error-wrapper">
                  <span class="error-text">Please complete all required fields.</span>
                </div>
              </div>
            </div>
          </div>

          <button class="add-btn" @click="addComponent">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M8 3v10M3 8h10"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
            Add
          </button>
        </template>

        <template v-else-if="itemInfo?.type === 'Routine'">
          <div
            v-for="(category, categoryIndex) in routineCategories"
            :key="categoryIndex"
            class="routine-category"
          >
            <div class="category-title">{{ category.title }}</div>

            <div
              v-for="(option, optionIndex) in category.options"
              :key="optionIndex"
              class="option-row"
              @dragover="handleDragOver"
              @drop="handleDrop($event, categoryIndex, optionIndex)"
            >
              <div class="option-input-line">
                <div
                  class="option-drag-handle"
                  draggable="true"
                  @dragstart="handleDragStart($event, categoryIndex, optionIndex)"
                  @dragend="handleDragEnd"
                >
                  <img :src="dragIcon" alt="drag" class="drag-icon" />
                </div>
                <div class="option-field">
                  <div class="option-input-wrapper">
                    <input
                      :value="option.value"
                      type="text"
                      class="option-input"
                      :class="{ 'input-error': option.hasError }"
                      placeholder="Value (max 100 characters)"
                      @input="(e) => handleOptionValueInput(categoryIndex, optionIndex, (e.target as HTMLInputElement).value)"
                      @mousedown.stop
                      @click.stop
                    />
                    <img v-if="option.hasError" :src="alertIcon" alt="alert" class="option-alert-icon" />
                  </div>
                </div>
                <button class="option-delete-btn" @click.stop="deleteOption(categoryIndex, optionIndex)">
                  <img :src="noIcon" alt="delete" class="option-delete-icon" />
                </button>
              </div>
              <div v-if="option.hasError" class="option-error-line">
                <span class="error-text">Please keep it under 100 characters</span>
              </div>
            </div>

            <button class="add-option-btn" @click="addOption(categoryIndex)">+ Add</button>
          </div>
        </template>
      </div>

      <div class="modal-footer">
        <div class="footer-buttons">
          <button class="cancel-btn" type="button" @click.stop="handleClose">Cancel</button>
          <button class="save-btn" type="button" :disabled="submitting" @click="handleSave">
            Save
          </button>
        </div>
      </div>
      </div>
    </div>
  </Teleport>
</template>
<script setup lang="ts">
import { ref, reactive, onMounted, onUnmounted } from 'vue'
import '@/styles/checklist-item-tags.css'
import { setSetValue } from '@/api/setvalue'
import fillIcon from '@/assets/Fill 1@2x.png'
import dragIcon from '@/assets/drag.png'
import downarrowIcon from '@/assets/downarrow@2x.png'
import noIcon from '@/assets/no.png'
import alertIcon from '@/assets/alert.png'
import ToastComponent from '@/components/ToastComponent.vue'
import { FIELD_LIMITS } from '@/constants/fieldLimits'
import { validateStringLength } from '@/utils/fieldLimitValidation'

const checklistOptionLimit = FIELD_LIMITS.checklist.optionValue

interface Component {
  name: string
  values: string
  fieldType: string
  nameHasError: boolean
  valuesHasError: boolean
  fieldTypeHasError: boolean
}

interface RoutineOption {
  value: string
  hasError: boolean
}

interface RoutineCategory {
  title: string
  options: RoutineOption[]
}

interface BaselineComponent {
  name: string
  values: string
  fieldType: string
}

interface ItemInfo {
  id: string
  title: string
  type: string
  values?: {
    condition: string[]
    location: string[]
    task: string[]
    time: string[]
  }
  components?: BaselineComponent[]
}

interface Props {
  itemInfo: ItemInfo
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'saved'): void
  (e: 'toast', data: { type: 'success' | 'warning' | 'error'; message: string }): void
}>()

const isOpen = ref(true)

const components = ref<Component[]>([
  {
    name: '',
    values: '',
    fieldType: 'single_choice',
    nameHasError: false,
    valuesHasError: false,
    fieldTypeHasError: false,
  },
])

const routineCategories = ref<RoutineCategory[]>([
  {
    title: 'Condition',
    options: [{ value: '', hasError: false }],
  },
  {
    title: 'Location',
    options: [{ value: '', hasError: false }],
  },
  {
    title: 'Task',
    options: [{ value: '', hasError: false }],
  },
  {
    title: 'Time',
    options: [{ value: '', hasError: false }],
  },
])


import { getChecklistDetail } from '@/api/itemdetail'

const fieldTypeOptions = [
  { value: 'single_choice', label: 'Single Choice' },
  { value: 'multiple_choice', label: 'Multiple Choice' },
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
]

const fieldTypeDropdownOpen = reactive<Record<number, boolean>>({})

const toastVisible = ref(false)
const toastType = ref<'success' | 'warning' | 'error'>('warning')
const toastMessage = ref('')
const submitting = ref(false)
let toastTimer: ReturnType<typeof setTimeout> | null = null

function showToastFn(type: 'success' | 'warning' | 'error', message: string): void {
  if (toastTimer) {
    clearTimeout(toastTimer)
  }
  toastType.value = type
  toastMessage.value = message
  toastVisible.value = true
  toastTimer = setTimeout(() => {
    toastVisible.value = false
  }, 3500)
}

function normalizeFieldType(fieldType: string): string {
  const key = fieldType?.trim().toLowerCase()
  const map: Record<string, string> = {
    single: 'single_choice',
    multiple: 'multiple_choice',
    single_choice: 'single_choice',
    multiple_choice: 'multiple_choice',
    text: 'text',
    number: 'number',
  }
  return map[key] || fieldType
}

function isFieldTypeSelected(current: string, optionValue: string): boolean {
  return normalizeFieldType(current) === optionValue
}

function getFieldTypeLabel(fieldType: string): string {
  const normalized = normalizeFieldType(fieldType)
  if (!normalized) return 'Select field type'
  const option = fieldTypeOptions.find((o) => o.value === normalized)
  return option?.label || 'Single Choice'
}

function fieldTypeRequiresValues(fieldType: string): boolean {
  const normalized = normalizeFieldType(fieldType)
  return normalized === 'single_choice' || normalized === 'multiple_choice'
}

function closeAllFieldTypeDropdowns(): void {
  Object.keys(fieldTypeDropdownOpen).forEach((key) => {
    fieldTypeDropdownOpen[Number(key)] = false
  })
}

function toggleFieldTypeDropdown(index: number): void {
  const wasOpen = fieldTypeDropdownOpen[index]
  closeAllFieldTypeDropdowns()
  fieldTypeDropdownOpen[index] = !wasOpen
}

function selectFieldType(index: number, value: string): void {
  if (components.value[index]) {
    components.value[index].fieldType = value
    components.value[index].fieldTypeHasError = false
    if (!fieldTypeRequiresValues(value)) {
      components.value[index].values = ''
      components.value[index].valuesHasError = false
    }
  }
  fieldTypeDropdownOpen[index] = false
}

function addComponent(): void {
  components.value.push({
    name: '',
    values: '',
    fieldType: 'single_choice',
    nameHasError: false,
    valuesHasError: false,
    fieldTypeHasError: false,
  })
}

function validateBaselineComponents(): boolean {
  let hasError = false

  components.value.forEach((comp) => {
    comp.nameHasError = !comp.name.trim()
    comp.valuesHasError = fieldTypeRequiresValues(comp.fieldType) && !comp.values.trim()
    comp.fieldTypeHasError = !comp.fieldType

    if (comp.nameHasError || comp.valuesHasError || comp.fieldTypeHasError) {
      hasError = true
    }
  })

  return !hasError
}

function deleteComponent(index: number): void {
  if (components.value.length > 1) {
    components.value.splice(index, 1)
  }
}

function addOption(categoryIndex: number): void {
  const category = routineCategories.value[categoryIndex]
  if (category) {
    category.options.push({
      value: '',
      hasError: false,
    })
  }
}

function deleteOption(categoryIndex: number, optionIndex: number): void {
  const category = routineCategories.value[categoryIndex]
  if (category && category.options.length > 1) {
    category.options.splice(optionIndex, 1)
  }
}

// Routine 拖拽相关状态
const draggingCategory = ref<number | null>(null)
const draggingIndex = ref<number | null>(null)

// 拖拽图像引用，用于清理
const dragImageRef = ref<HTMLElement | null>(null)

// Routine 开始拖拽
function handleDragStart(event: DragEvent, categoryIndex: number, optionIndex: number): void {
  const target = event.target as HTMLElement
  if (!target.closest('.option-drag-handle')) {
    event.preventDefault()
    return
  }

  draggingCategory.value = categoryIndex
  draggingIndex.value = optionIndex
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move'
    // 获取被拖动的行元素（使用closest确保获取到正确的行）
    const target = event.target as HTMLElement
    const rowEl = target.closest('.option-row') as HTMLElement

    // 获取输入框的值
    const inputEl = rowEl?.querySelector('.option-input') as HTMLInputElement | null
    const inputValue = inputEl?.value || ''

    // 创建拖拽图像
    const dragImage = document.createElement('div')
    dragImage.style.position = 'fixed'
    dragImage.style.top = '-1000px'
    dragImage.style.left = '-1000px'
    dragImage.style.opacity = '1'
    dragImage.style.width = '480px'
    dragImage.style.minWidth = '350px'
    dragImage.style.height = '48px'
    dragImage.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.25)'
    dragImage.style.borderRadius = '10px'
    dragImage.style.background = '#ffffff'
    dragImage.style.padding = '8px 16px'
    dragImage.style.boxSizing = 'border-box'
    dragImage.style.zIndex = '99999'
    dragImage.style.display = 'flex'
    dragImage.style.alignItems = 'center'
    dragImage.style.gap = '12px'
    dragImage.style.border = '1px solid #e0e0e0'

    // 添加拖拽图标（六个点）
    const dotsContainer = document.createElement('div')
    dotsContainer.style.width = '22px'
    dotsContainer.style.height = '26px'
    dotsContainer.style.display = 'grid'
    dotsContainer.style.gridTemplateColumns = 'repeat(2, 1fr)'
    dotsContainer.style.gridTemplateRows = 'repeat(3, 1fr)'
    dotsContainer.style.gap = '4px'
    dotsContainer.style.padding = '4px'
    for (let i = 0; i < 6; i++) {
      const dot = document.createElement('span')
      dot.style.width = '5px'
      dot.style.height = '5px'
      dot.style.borderRadius = '50%'
      dot.style.background = '#666'
      dotsContainer.appendChild(dot)
    }
    dragImage.appendChild(dotsContainer)

    // 添加输入框样式的显示
    const inputDisplay = document.createElement('div')
    inputDisplay.style.flex = '1'
    inputDisplay.style.minWidth = '200px'
    inputDisplay.style.height = '34px'
    inputDisplay.style.padding = '0 16px'
    inputDisplay.style.border = '1px solid #e0e0e0'
    inputDisplay.style.borderRadius = '17px'
    inputDisplay.style.fontSize = '14px'
    inputDisplay.style.color = '#000'
    inputDisplay.style.background = '#ffffff'
    inputDisplay.style.display = 'flex'
    inputDisplay.style.alignItems = 'center'
    inputDisplay.style.fontFamily = "-apple-system, BlinkMacSystemFont, system-ui, sans-serif"
    inputDisplay.textContent = inputValue || 'Value'
    inputDisplay.style.boxSizing = 'border-box'
    dragImage.appendChild(inputDisplay)

    // 添加删除图标（×）
    const deleteIconEl = document.createElement('span')
    deleteIconEl.style.width = '28px'
    deleteIconEl.style.height = '28px'
    deleteIconEl.style.display = 'flex'
    deleteIconEl.style.alignItems = 'center'
    deleteIconEl.style.justifyContent = 'center'
    deleteIconEl.style.fontSize = '24px'
    deleteIconEl.style.color = '#666'
    deleteIconEl.textContent = '×'
    dragImage.appendChild(deleteIconEl)

    // 强制浏览器渲染
    document.body.appendChild(dragImage)
    void dragImage.offsetWidth // 强制重排

    // 设置拖拽图像
    event.dataTransfer.setDragImage(dragImage, 10, 24)

    // 保存引用以便清理
    dragImageRef.value = dragImage

    // 拖拽结束后移除临时元素
    const cleanup = () => {
      if (dragImageRef.value) {
        dragImageRef.value.remove()
        dragImageRef.value = null
      }
      document.removeEventListener('dragend', cleanup)
    }
    document.addEventListener('dragend', cleanup)
  }
}

// Baseline 拖拽相关状态
const draggingComponentIndex = ref<number | null>(null)

// Baseline 开始拖拽
function handleComponentDragStart(event: DragEvent, index: number): void {
  const target = event.target as HTMLElement
  if (!target.closest('.component-drag-handle')) {
    event.preventDefault()
    return
  }

  draggingComponentIndex.value = index
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move'
    // 获取组件信息
    const component = components.value[index]
    const componentName = component?.name || ''
    const values = component?.values || ''
    const fieldType = component?.fieldType || ''

    // 创建拖拽图像（多行显示，简洁样式）
    const dragImage = document.createElement('div')
    dragImage.style.position = 'fixed'
    dragImage.style.top = '-1000px'
    dragImage.style.left = '-1000px'
    dragImage.style.opacity = '1'
    dragImage.style.width = '480px'
    dragImage.style.minWidth = '350px'
    dragImage.style.background = '#ffffff'
    dragImage.style.padding = '8px 12px'
    dragImage.style.boxSizing = 'border-box'
    dragImage.style.zIndex = '99999'
    dragImage.style.display = 'flex'
    dragImage.style.flexDirection = 'column'
    dragImage.style.gap = '8px'

    // 添加头部（图标和标题）
    const headerRow = document.createElement('div')
    headerRow.style.display = 'flex'
    headerRow.style.alignItems = 'center'
    headerRow.style.gap = '12px'

    // 添加拖拽图标（六个点）
    const dotsContainer = document.createElement('div')
    dotsContainer.style.width = '22px'
    dotsContainer.style.height = '26px'
    dotsContainer.style.display = 'grid'
    dotsContainer.style.gridTemplateColumns = 'repeat(2, 1fr)'
    dotsContainer.style.gridTemplateRows = 'repeat(3, 1fr)'
    dotsContainer.style.gap = '4px'
    dotsContainer.style.padding = '4px'
    for (let i = 0; i < 6; i++) {
      const dot = document.createElement('span')
      dot.style.width = '5px'
      dot.style.height = '5px'
      dot.style.borderRadius = '50%'
      dot.style.background = '#666'
      dotsContainer.appendChild(dot)
    }
    headerRow.appendChild(dotsContainer)

    // 添加序列号和Component标签
    const titleSection = document.createElement('div')
    titleSection.style.display = 'flex'
    titleSection.style.alignItems = 'center'
    titleSection.style.gap = '8px'

    const numberSpan = document.createElement('span')
    numberSpan.style.fontSize = '14px'
    numberSpan.style.fontWeight = '600'
    numberSpan.style.color = '#333'
    numberSpan.textContent = `#${index + 1}`
    titleSection.appendChild(numberSpan)

    const requiredMark = document.createElement('span')
    requiredMark.style.color = '#dc2626'
    requiredMark.textContent = '*'
    titleSection.appendChild(requiredMark)

    const labelSpan = document.createElement('span')
    labelSpan.style.fontSize = '14px'
    labelSpan.style.color = '#666'
    labelSpan.textContent = 'Component'
    titleSection.appendChild(labelSpan)
    headerRow.appendChild(titleSection)

    // 添加删除图标（×）
    const deleteIconEl = document.createElement('span')
    deleteIconEl.style.width = '28px'
    deleteIconEl.style.height = '28px'
    deleteIconEl.style.display = 'flex'
    deleteIconEl.style.alignItems = 'center'
    deleteIconEl.style.justifyContent = 'center'
    deleteIconEl.style.fontSize = '24px'
    deleteIconEl.style.color = '#666'
    deleteIconEl.textContent = '×'
    deleteIconEl.style.marginLeft = 'auto'
    headerRow.appendChild(deleteIconEl)

    dragImage.appendChild(headerRow)

    // 添加输入框内容（Component名称）
    const nameInput = document.createElement('div')
    nameInput.style.width = '100%'
    nameInput.style.height = '36px'
    nameInput.style.padding = '0 16px'
    nameInput.style.border = '1px solid #e0e0e0'
    nameInput.style.borderRadius = '18px'
    nameInput.style.fontSize = '14px'
    nameInput.style.color = '#000'
    nameInput.style.background = '#ffffff'
    nameInput.style.display = 'flex'
    nameInput.style.alignItems = 'center'
    nameInput.style.fontFamily = "-apple-system, BlinkMacSystemFont, system-ui, sans-serif"
    nameInput.textContent = componentName || 'Component (max 300 characters)'
    nameInput.style.boxSizing = 'border-box'
    dragImage.appendChild(nameInput)

    // 添加Values标签和输入框
    if (fieldTypeRequiresValues(fieldType)) {
      const valuesRow = document.createElement('div')
      valuesRow.style.display = 'flex'
      valuesRow.style.flexDirection = 'column'
      valuesRow.style.gap = '4px'

      const valuesLabel = document.createElement('div')
      valuesLabel.style.display = 'flex'
      valuesLabel.style.alignItems = 'center'
      valuesLabel.style.gap = '4px'

      const valuesRequired = document.createElement('span')
      valuesRequired.style.color = '#dc2626'
      valuesRequired.textContent = '*'
      valuesLabel.appendChild(valuesRequired)

      const valuesText = document.createElement('span')
      valuesText.style.fontSize = '13px'
      valuesText.style.color = '#666'
      valuesText.textContent = 'Values'
      valuesLabel.appendChild(valuesText)
      valuesRow.appendChild(valuesLabel)

      const valuesInput = document.createElement('div')
      valuesInput.style.width = '100%'
      valuesInput.style.height = '36px'
      valuesInput.style.padding = '0 16px'
      valuesInput.style.border = '1px solid #e0e0e0'
      valuesInput.style.borderRadius = '18px'
      valuesInput.style.fontSize = '14px'
      valuesInput.style.color = '#000'
      valuesInput.style.background = '#ffffff'
      valuesInput.style.display = 'flex'
      valuesInput.style.alignItems = 'center'
      valuesInput.style.fontFamily = "-apple-system, BlinkMacSystemFont, system-ui, sans-serif"
      valuesInput.textContent = values || 'No values'
      valuesInput.style.boxSizing = 'border-box'
      valuesRow.appendChild(valuesInput)
      dragImage.appendChild(valuesRow)
    }

    // 添加Field Type标签和输入框
    const fieldTypeRow = document.createElement('div')
    fieldTypeRow.style.display = 'flex'
    fieldTypeRow.style.flexDirection = 'column'
    fieldTypeRow.style.gap = '4px'

    const fieldTypeLabel = document.createElement('div')
    fieldTypeLabel.style.display = 'flex'
    fieldTypeLabel.style.alignItems = 'center'
    fieldTypeLabel.style.gap = '4px'

    const fieldTypeRequired = document.createElement('span')
    fieldTypeRequired.style.color = '#dc2626'
    fieldTypeRequired.textContent = '*'
    fieldTypeLabel.appendChild(fieldTypeRequired)

    const fieldTypeText = document.createElement('span')
    fieldTypeText.style.fontSize = '13px'
    fieldTypeText.style.color = '#666'
    fieldTypeText.textContent = 'Field Type'
    fieldTypeLabel.appendChild(fieldTypeText)
    fieldTypeRow.appendChild(fieldTypeLabel)

    const fieldTypeInput = document.createElement('div')
    fieldTypeInput.style.width = '100%'
    fieldTypeInput.style.height = '36px'
    fieldTypeInput.style.padding = '0 16px'
    fieldTypeInput.style.border = '1px solid #e0e0e0'
    fieldTypeInput.style.borderRadius = '18px'
    fieldTypeInput.style.fontSize = '14px'
    fieldTypeInput.style.color = '#000'
    fieldTypeInput.style.background = '#ffffff'
    fieldTypeInput.style.display = 'flex'
    fieldTypeInput.style.alignItems = 'center'
    fieldTypeInput.style.fontFamily = "-apple-system, BlinkMacSystemFont, system-ui, sans-serif"
    fieldTypeInput.textContent = fieldType || 'Select field type'
    fieldTypeInput.style.boxSizing = 'border-box'
    fieldTypeRow.appendChild(fieldTypeInput)
    dragImage.appendChild(fieldTypeRow)

    // 强制浏览器渲染
    document.body.appendChild(dragImage)
    void dragImage.offsetWidth // 强制重排

    // 设置拖拽图像（中心点在顶部）
    event.dataTransfer.setDragImage(dragImage, 10, 26)

    // 保存引用以便清理
    dragImageRef.value = dragImage

    // 拖拽结束后移除临时元素
    const cleanup = () => {
      if (dragImageRef.value) {
        dragImageRef.value.remove()
        dragImageRef.value = null
      }
      document.removeEventListener('dragend', cleanup)
    }
    document.addEventListener('dragend', cleanup)
  }
}

// Baseline 拖拽经过
function handleComponentDragOver(event: DragEvent): void {
  event.preventDefault()
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = 'move'
  }
}

// Baseline 放置
function handleComponentDrop(event: DragEvent, targetIndex: number): void {
  event.preventDefault()

  if (draggingComponentIndex.value === null) return
  if (draggingComponentIndex.value === targetIndex) return

  const draggedComponent = components.value[draggingComponentIndex.value]
  if (!draggedComponent) return

  components.value.splice(draggingComponentIndex.value, 1)
  components.value.splice(targetIndex, 0, draggedComponent)

  draggingComponentIndex.value = null
}

// Baseline 拖拽结束
function handleComponentDragEnd(): void {
  draggingComponentIndex.value = null
  // 额外的清理保障
  if (dragImageRef.value) {
    dragImageRef.value.remove()
    dragImageRef.value = null
  }
}

// 拖拽经过
function handleDragOver(event: DragEvent): void {
  event.preventDefault()
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = 'move'
  }
}

// 放置
function handleDrop(event: DragEvent, targetCategoryIndex: number, targetOptionIndex: number): void {
  event.preventDefault()

  if (draggingCategory.value === null || draggingIndex.value === null) return
  if (draggingCategory.value !== targetCategoryIndex) return

  const category = routineCategories.value[draggingCategory.value]
  if (!category) return

  const draggedOption = category.options[draggingIndex.value]
  if (!draggedOption) return

  category.options.splice(draggingIndex.value, 1)
  category.options.splice(targetOptionIndex, 0, draggedOption)

  draggingCategory.value = null
  draggingIndex.value = null
}

// 拖拽结束
function handleDragEnd(): void {
  draggingCategory.value = null
  draggingIndex.value = null
  // 额外的清理保障
  if (dragImageRef.value) {
    dragImageRef.value.remove()
    dragImageRef.value = null
  }
}

function handleComponentNameInput(index: number, value: string): void {
  const component = components.value[index]
  if (!component) return

  const isOverLimit = value.length > 300
  if (isOverLimit) {
    component.name = value.slice(0, 300)
    component.nameHasError = true
  } else {
    component.name = value
    component.nameHasError = false
  }
}

function handleComponentValuesInput(index: number, value: string): void {
  const component = components.value[index]
  if (!component) return

  const isOverLimit = value.length > 300
  if (isOverLimit) {
    component.values = value.slice(0, 300)
    component.valuesHasError = true
  } else {
    component.values = value
    component.valuesHasError = false
  }
}

function handleOptionValueInput(categoryIndex: number, optionIndex: number, value: string): void {
  const category = routineCategories.value[categoryIndex]
  if (!category) return

  const option = category.options[optionIndex]
  if (!option) return

  const isOverLimit = value.length > 100
  if (isOverLimit) {
    option.value = value.slice(0, 100)
    option.hasError = true
  } else {
    option.value = value
    option.hasError = false
  }
}

function handleClose(): void {
  if (!isOpen.value) return
  isOpen.value = false
  emit('close')
}



async function handleSave(): Promise<void> {
  if (!props.itemInfo?.id) {
    showToastFn('error', 'Checklist id is required.')
    return
  }

  if (props.itemInfo?.type === 'Baseline') {
    if (!validateBaselineComponents()) {
      showToastFn('warning', 'Please complete all required fields.')
      return
    }

    submitting.value = true
    try {
      const values = {
        condition: [],
        location: [],
        task: [],
        time: [],
        components: components.value.map((comp) => ({
          component: comp.name,
          values: fieldTypeRequiresValues(comp.fieldType)
            ? comp.values.split(',').map((v: string) => v.trim()).filter((v: string) => v)
            : [],
          field_type: comp.fieldType,
        })),
      }

      const response = await setSetValue({
        id: Number(props.itemInfo.id),
        values,
      })

      if (response.code === 200) {
        emit('saved')
        emit('toast', { type: 'success', message: 'Successfully saved' })
        setTimeout(() => handleClose(), 300)
      } else {
        showToastFn('error', response.msg || 'Failed to save')
      }
    } catch (error) {
      console.error('Save error:', error)
      showToastFn('error', 'Failed to save')
    } finally {
      submitting.value = false
    }
  } else if (props.itemInfo?.type === 'Routine') {
    let hasError = false

    routineCategories.value.forEach((category) => {
      category.options.forEach((option) => {
        if (!option.value.trim()) {
          option.hasError = true
          hasError = true
        } else {
          option.hasError = false
        }
      })
    })

    if (hasError) {
      showToastFn('warning', 'Please complete all required fields.')
      return
    }

    for (const category of routineCategories.value) {
      for (const option of category.options) {
        const value = option.value.trim()
        if (!value) continue
        const limitErr = validateStringLength(
          value,
          checklistOptionLimit,
          `${category.title} value`,
        )
        if (limitErr) {
          option.hasError = true
          showToastFn('warning', limitErr)
          return
        }
      }
    }

    submitting.value = true
    try {
      const values = {
        condition:
          routineCategories.value[0]?.options.map((opt) => opt.value.trim()).filter((v) => v) || [],
        location:
          routineCategories.value[1]?.options.map((opt) => opt.value.trim()).filter((v) => v) || [],
        task:
          routineCategories.value[2]?.options.map((opt) => opt.value.trim()).filter((v) => v) || [],
        time:
          routineCategories.value[3]?.options.map((opt) => opt.value.trim()).filter((v) => v) || [],
      }

      const response = await setSetValue({
        id: Number(props.itemInfo.id),
        values,
      })

      if (response.code === 200) {
        emit('saved')
        emit('toast', { type: 'success', message: 'Successfully saved' })
        setTimeout(() => handleClose(), 300)
      } else {
        showToastFn('error', response.msg || 'Failed to save')
      }
    } catch (error) {
      console.error('Save error:', error)
      showToastFn('error', 'Failed to save')
    } finally {
      submitting.value = false
    }
  }
}

function fillValues(values: { condition?: string[], location?: string[], task?: string[], time?: string[] }) {
  // 填充 Condition
  if (values.condition && values.condition.length > 0 && routineCategories.value[0]) {
    routineCategories.value[0].options = values.condition.map(v => ({ value: v, hasError: false }))
  }
  // 填充 Location
  if (values.location && values.location.length > 0 && routineCategories.value[1]) {
    routineCategories.value[1].options = values.location.map(v => ({ value: v, hasError: false }))
  }
  // 填充 Task
  if (values.task && values.task.length > 0 && routineCategories.value[2]) {
    routineCategories.value[2].options = values.task.map(v => ({ value: v, hasError: false }))
  }
  // 填充 Time
  if (values.time && values.time.length > 0 && routineCategories.value[3]) {
    routineCategories.value[3].options = values.time.map(v => ({ value: v, hasError: false }))
  }
}

onMounted(async () => {
  document.addEventListener('click', closeAllDropdowns)

  console.log('SetValue mounted:', props.itemInfo)

  // 优先从 props 获取源数据
  if (props.itemInfo?.type === 'Routine' && props.itemInfo?.values) {
    fillValues(props.itemInfo.values)
    return
  }

  if (props.itemInfo?.type === 'Baseline' && props.itemInfo?.components) {
    components.value = props.itemInfo.components.map(comp => ({
      ...comp,
      fieldType: normalizeFieldType(comp.fieldType),
      nameHasError: false,
      valuesHasError: false,
      fieldTypeHasError: false,
    }))
    return
  }

  // 如果 props 中没有数据，从后端 API 获取
  if (props.itemInfo?.id) {
    try {
      // 直接使用 getChecklistDetail API 获取数据（getSetValue API 返回 404）
      const detailResponse = await getChecklistDetail({ id: Number(props.itemInfo.id) })
      console.log('detailResponse:', detailResponse)
      if (detailResponse.code === 200 && detailResponse.data) {
        const data = detailResponse.data
        console.log('ChecklistDetail data for Baseline:', data)
        const values = data.values as { components?: Array<{ component?: string, name?: string, values?: string[], field_type?: string, fieldType?: string }> }

        if (props.itemInfo.type === 'Routine') {
          // 处理 Routine 类型的数据
          if (values && values.components) {
            const routineValues: { condition?: string[], location?: string[], task?: string[], time?: string[] } = {}
            values.components.forEach((comp) => {
              const type = comp.field_type?.toLowerCase() || comp.component?.toLowerCase()
              if (type === 'condition') {
                routineValues.condition = comp.values
              } else if (type === 'location') {
                routineValues.location = comp.values
              } else if (type === 'task') {
                routineValues.task = comp.values
              } else if (type === 'time') {
                routineValues.time = comp.values
              }
            })
            fillValues(routineValues)
          } else if (values && typeof values === 'object' && !Array.isArray(values)) {
            fillValues(values as { condition?: string[], location?: string[], task?: string[], time?: string[] })
          }
        } else if (props.itemInfo.type === 'Baseline') {
          // 处理 Baseline 类型的数据
          interface BackendComponent {
            component?: string
            values?: string[]
            field_type?: string
            fieldType?: string
          }
          let sourceComponents: BackendComponent[] = []

          // 尝试从 data.values.components 获取
          console.log('data.values:', data.values)
          console.log('data.checklist_values:', data.checklist_values)
          if (data.values && data.values.components && Array.isArray(data.values.components)) {
            sourceComponents = data.values.components as BackendComponent[]
          } else if (data.checklist_values && data.checklist_values.components && Array.isArray(data.checklist_values.components)) {
            // 如果没有，尝试从 data.checklist_values.components 获取
            sourceComponents = data.checklist_values.components as BackendComponent[]
          }

          console.log('Found sourceComponents:', sourceComponents)
          if (sourceComponents.length > 0) {
            components.value = sourceComponents.map((comp) => ({
              name: comp.component || '',
              values: comp.values ? (Array.isArray(comp.values) ? comp.values.join(', ') : String(comp.values)) : '',
              fieldType: normalizeFieldType(comp.field_type || comp.fieldType || 'single_choice'),
              nameHasError: false,
              valuesHasError: false,
              fieldTypeHasError: false,
            }))
          }
        }
      }
    } catch (error) {
      console.error('Failed to get set value:', error)
    }
  }
})

onUnmounted(() => {
  document.removeEventListener('click', closeAllDropdowns)
  if (toastTimer) {
    clearTimeout(toastTimer)
  }
  // 确保卸载时清理拖拽图像
  if (dragImageRef.value) {
    dragImageRef.value.remove()
    dragImageRef.value = null
  }
})

function closeAllDropdowns(event: MouseEvent): void {
  const target = event.target as HTMLElement
  if (!target.closest('.field-select-wrapper')) {
    closeAllFieldTypeDropdowns()
  }
}
</script>

<style scoped>
.set-value-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
  padding: 20px;
}

.set-value-modal {
  background-color: #ffffff;
  border-radius: 12px;
  width: 530px;
  height: 670px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}
.modal-baseline {
  height: 670px;
}
.modal-routine {
  height: 670px;
}
@media (max-width: 576px) {
  .set-value-modal {
    width: 100%;
    height: 90vh;
    min-height: 670px;
  }
}

.modal-header {

  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px 24px 0;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
  background-color: #ffffff;
}

.title-section {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  flex: 1;
  flex-wrap: wrap;
}

.modal-title {
  font-size: 16px;
  font-weight: 700;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  margin: 0;
  word-break: break-word;
  max-width: 100%;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
}

.close-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.header-spacing {
  height: 16px;
  background-color: #ffffff;
}

.modal-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 24px 40px;
}

.component-block {
  margin-bottom: 16px;
  cursor: default;
}

.component-block:last-child {
  margin-bottom: 16px;
}

.component-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  justify-content: space-between;
  width: 100%;
  box-sizing: border-box;
}

.component-header-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.component-drag-handle {
  cursor: grab;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
}

.component-drag-handle:active {
  cursor: grabbing;
}

.drag-icon {
  width: 22px;
  height: 22px;
  object-fit: contain;
}

.component-title-section {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.component-number {
  font-size: 16px;
  font-weight: 600;
  color: #000000;
}

.required-mark {
  color: #ff4d4f;
  font-size: 16px;
  font-weight: 600;
}

.component-label {
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #000000;
  text-align: left;
}

.component-title-section .required-mark {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.component-delete-btn {
  background: none;
  border: none;
  cursor: pointer;
  width: 22px;
  height: 22px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.delete-icon {
  width: 22px;
  height: 22px;
}

.component-fields {
  padding-left: 63px;
}

.component-field {
  margin-bottom: 20px;
}

.component-field:last-child {
  margin-bottom: 20px;
}

.component-field .error-wrapper {
  display: block;
  margin-top: 4px;
  width: 100%;
}

.component-field.has-error .error-wrapper {
  margin-top: 4px !important;
}

.component-field.has-error .error-text {
  visibility: visible;
  opacity: 1;
  transition: visibility 0s, opacity 0.16s;
}

.field-label {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 16px 0;
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #000000;
  text-align: left;
}

.field-label .required-mark {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.field-input {
  width: 420px;
  height: 32px;
  padding: 0 16px;
  border: 1px solid #e0e0e0;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.field-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 420px;
}

.field-alert-icon {
  position: absolute;
  right: 12px;
  width: 16px;
  height: 16px;
  pointer-events: none;
  flex-shrink: 0;
}

.field-alert-icon-select {
  right: 36px;
}

.field-input-wrapper .field-select-wrapper {
  flex: 1;
  width: 100%;
}

.field-input-wrapper .field-select-wrapper .field-input {
  width: 100%;
}

.field-input-wrapper .field-select-wrapper .field-input.input-error {
  padding-right: 64px;
}

.field-input-wrapper .field-input {
  padding-right: 42px;
}

.field-input:focus {
  border-color: #666666;
}

.field-input::placeholder {
  color: #cccccc;
}

.field-input.is-placeholder {
  color: #cccccc;
}

.input-error {
  border-color: #ff4d4f !important;
  background-color: #ffffff !important;
}

.error-text {
  display: block;
  color: #ff4d4f;
  font-size: 12px;
  width: 100%;
}

.field-select-wrapper {
  position: relative;
  width: 420px;
}

.field-select-wrapper:focus-within .field-input:not(.input-error) {
  border-color: #666666;
}

.select-arrow {
  position: absolute;
  right: 16px;
  top: 50%;
  width: 16px;
  height: 16px;
  max-width: 16px;
  max-height: 16px;
  object-fit: contain;
  opacity: 0.55;
  pointer-events: none;
  transform: translateY(-50%);
  transition: transform 0.2s ease;
}

.select-arrow--open {
  transform: translateY(-50%) rotate(180deg);
}

.dropdown-panel {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin-top: 8px;
  padding: 4px 0;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.dropdown-option {
  padding: 8px 16px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.2s;
  color: #000000;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
}

.dropdown-option:hover {
  background-color: #f5f5f5;
}

.dropdown-panel .dropdown-option.active {
  font-weight: 600;
}

.dropdown-panel .dropdown-option.active span {
  font-weight: 600;
}

.add-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background-color: transparent;
  border: none;
  color: #00aeef;
  font-size: 14px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  cursor: pointer;
  margin-top: 16px;
  margin-bottom: 16px;
}

.add-btn:hover {
  color: #0095d0;
}

.routine-category {
  margin-bottom: 0;
}

.routine-category:not(:first-child) {
  margin-top: 16px;
}

.category-title {
  font-size: 14px;
  font-weight: 700;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  margin-bottom: 16px;
}

.option-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 16px;
  cursor: default;
  position: relative;
}

.option-input-line {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
}

.option-field {
  flex: 1;
  min-width: 0;
  position: relative;
  margin-bottom: 0;
}

.option-error-line {
  padding-left: 40px;
  padding-right: 38px;
}

.option-error-line .error-text {
  display: block;
  color: #ff4d4f;
  font-size: 12px;
  line-height: 16px;
}

.option-drag-handle {
  cursor: grab;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.option-drag-handle:active {
  cursor: grabbing;
}

.option-input {
  width: 100%;
  height: 32px;
  padding: 0 16px;
  border: 1px solid #e0e0e0;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.option-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.option-alert-icon {
  position: absolute;
  right: 10px;
  width: 16px;
  height: 16px;
  pointer-events: none;
  flex-shrink: 0;
}

.option-input-wrapper .option-input {
  padding-right: 28px;
}

.option-input:focus {
  border-color: #666666;
}

.option-input::placeholder {
  color: #cccccc;
}

.option-input.input-error {
  border-color: #ff4d4f;
  background-color: #ffffff;
}


.option-delete-btn {
  background: none;
  border: none;
  cursor: pointer;
  width: 22px;
  height: 22px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.option-delete-icon {
  width: 22px;
  height: 22px;
}

.add-option-btn {
  background: none;
  border: none;
  color: #00aeef;
  font-size: 14px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  cursor: pointer;
  margin-bottom: -160px;
  display: inline-block;
}

.add-option-btn:hover {
  color: #0095d0;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  flex-shrink: 0;
  margin-top: 16px;
  position: relative;
  z-index: 1;
  background-color: #ffffff;
}
.footer-buttons {
  display: flex;
  gap: 16px;
  margin-left: auto;
}

.cancel-btn {
  width: 80px;
  height: 32px;
  padding: 0;
  background-color: #ffffff;
  border: 1px solid #000000;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-btn:hover {
  background-color: #000000;
  color: #ffffff;
}

.save-btn {
  width: 80px;
  height: 32px;
  padding: 0;
  background-color: #000;
  border: none;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #ffffff;
  cursor: pointer;
  transition: background-color 0.2s;
}

.save-btn:hover {
  background-color: #484848;
}
</style>
